import { useEffect, useRef, useState, createContext, useContext } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowLeft,
  ArrowUpRight,
  Instagram,
  Mail,
  Globe as GlobeIcon,
  MapPin,
  Users,
  Sparkles,
  Film,
  Video,
  Compass,
  ShieldCheck,
  Camera,
  Award,
  Palette,
  Play,
} from "lucide-react";
import useLenis from "../hooks/useLenis";
import { KIT_ASSETS, KIT_CHANNELS } from "../data/kit";
import { SITE } from "../data/site";
import { CHANNELS } from "../data/site";
import { BRAND_LOGOS } from "../data/logos";
import { useT } from "@/i18n/I18nContext";
import Navigation from "../components/site/Navigation";

/* -------------------------------------------------------------------------- */
/*  English-only copy — this page is a premium presentation for international  */
/*  marketing managers. No i18n toggle. No localization branching.             */
/* -------------------------------------------------------------------------- */

const COPY_EN = {
  header: { back: "Back to site", meta: "Media Kit · 2026 Edition" },
  hero: {
    eyebrow: "Terra & Trilhos",
    title_l: "Brand Partnership",
    title_r: "Kit",
    subtitle: "Authentic storytelling. Real adventures. Premium content.",
    cta: "Work With Us",
  },
  who: {
    overline: "01 · who we are",
    title: "A Portuguese creative house.",
    p1:
      "Terra & Trilhos is a Portuguese media brand producing premium long-form content across Outdoor, Adventure, Travel, DIY & Home Renovation, and Technology.",
    p2:
      "An independent team building long-term partnerships with brands that value authenticity, cinematography, and lasting presence.",
    role: "Eduardo Moreira · Founder & Creator",
    location: "Portugal · 2026",
  },
  channels: {
    overline: "02 · our channels",
    title: "Two channels. One voice.",
    caption: "Two long-form YouTube channels — different worlds, same craft.",
    work_title: "Featured work",
    work_caption: "A selection of our best-performing films. Press play to watch.",
  },
  audience: {
    overline: "03 · audience",
    title: "An international, qualified audience.",
    countries_label: "Top markets",
    demo_label: "Demographic profile",
    interests_label: "Interest areas",
    male: "Predominantly male",
    age: "35–64 years old",
    countries: [
      "Portugal",
      "Brazil",
      "France",
      "Switzerland",
      "United Kingdom",
      "United States",
    ],
    interests: [
      "DIY",
      "Outdoor",
      "Construction",
      "Adventure",
      "Technology",
      "Renewable Energy",
      "Travel",
    ],
  },
  performance: {
    overline: "04 · performance",
    title: "Lifetime performance.",
    caption: "Source · YouTube Studio, all-time metrics.",
    labels: {
      subs: "Subscribers",
      views: "Lifetime Views",
      watch: "Watch Hours",
      impressions: "Impressions",
      ctr: "Click-through Rate (CTR)",
    },
    ctr_desc: "Percentage of people who clicked on the video after seeing its thumbnail.",
  },
  content: {
    overline: "05 · content pillars",
    title: "What we produce.",
    pillars: [
      "Outdoor Adventure",
      "DIY",
      "Technology",
      "Travel",
      "Home Renovation",
      "Product Reviews",
      "Drone Cinematography",
    ],
  },
  trusted: {
    overline: "06 · trusted by",
    title: "Trusted by leading international brands.",
    wordmarks: ["REOLINK", "ALLPOWERS", "EINHELL", "SOPREMA", "BARBOT", "BLUETTI"],
  },
  why: {
    overline: "07 · why brands choose us",
    title: "Ten reasons.",
    items: [
      "Authentic Storytelling",
      "Professional Cinematography",
      "Long-form Evergreen Content",
      "High Audience Trust",
      "Natural Product Integration",
      "Drone Production",
      "Product Photography",
      "International Event Coverage",
      "Portuguese Market Expertise",
      "European Audience",
    ],
  },
  opps: {
    overline: "08 · partnership opportunities",
    title: "Available formats.",
    items: [
      "Dedicated YouTube Video",
      "Integrated Sponsorship",
      "Product Review",
      "Long-Term Partnership",
      "Trade Show Coverage",
      "Booth Tour",
      "Event Coverage",
      "Photography",
      "Drone Content",
      "Instagram Reels",
      "YouTube Shorts",
      "Affiliate Campaigns",
    ],
  },
  events: {
    overline: "09 · trade shows",
    title: "International event coverage.",
    text:
      "Available for international trade shows, product launches, booth tours, interviews, and live event storytelling.",
    confirmed_label: "Confirmed",
    future_label: "Future editions",
    future: ["CES Las Vegas", "Eurobike", "Outdoor by ISPO"],
  },
  contact: {
    overline: "10 · let's talk",
    title_l: "Let's build something",
    title_r: "remarkable together.",
    website_label: "Website",
    email_label: "Email",
    ig_label: "Instagram",
    website: "terraetrilhos.com",
    website_href: "https://terraetrilhos.com",
    email: "parcerias@terraetrilhos.com",
    ig: "@terraetrilhos",
    ig_href: "https://www.instagram.com/trilhosemfuria/",
    qr_hint: "Scan · open the site",
  },
};

const COPY_PT = {
  header: { back: "Voltar ao site", meta: "Media Kit · Edição 2026" },
  hero: {
    eyebrow: "Terra & Trilhos",
    title_l: "Kit de",
    title_r: "Parcerias",
    subtitle: "Storytelling autêntico. Aventura real. Conteúdo premium.",
    cta: "Trabalhar Connosco",
  },
  who: {
    overline: "01 · quem somos",
    title: "Uma casa criativa portuguesa.",
    p1:
      "A Terra & Trilhos é uma media brand portuguesa dedicada à produção de conteúdo long-form premium — histórias reais, filmadas e editadas com padrões cinematográficos, nas áreas de Outdoor, Aventura, Viagem, DIY & Restauro, Tecnologia e Storytelling.",
    p2:
      "Uma equipa independente focada em construir parcerias de longo prazo com marcas que valorizam autenticidade, cinematografia e presença duradoura.",
    role: "Eduardo Moreira · Fundador & Criador",
    location: "Portugal · 2026",
  },
  channels: {
    overline: "02 · os nossos canais",
    title: "Dois canais. Uma só voz.",
    caption: "Dois canais long-form no YouTube — mundos diferentes, o mesmo ofício.",
    work_title: "Trabalho em destaque",
    work_caption: "Uma seleção dos nossos filmes com melhor desempenho. Carregue para ver.",
  },
  audience: {
    overline: "03 · audiência",
    title: "Uma audiência internacional e qualificada.",
    countries_label: "Principais mercados",
    demo_label: "Perfil demográfico",
    interests_label: "Áreas de interesse",
    male: "Predominantemente masculino",
    age: "35–64 anos",
    countries: ["Portugal", "Brasil", "França", "Suíça", "Reino Unido", "Estados Unidos"],
    interests: [
      "DIY",
      "Outdoor",
      "Construção",
      "Aventura",
      "Tecnologia",
      "Energia Renovável",
      "Viagem",
    ],
  },
  performance: {
    overline: "04 · performance",
    title: "Performance total.",
    caption: "Fonte · YouTube Studio, métricas totais desde o início.",
    labels: {
      subs: "Subscritores",
      views: "Visualizações totais",
      watch: "Horas assistidas",
      impressions: "Impressões",
      ctr: "Taxa de cliques (CTR)",
    },
    ctr_desc: "Percentagem de pessoas que clicaram no vídeo após visualizarem a miniatura.",
  },
  content: {
    overline: "05 · pilares de conteúdo",
    title: "O que produzimos.",
    pillars: [
      "Aventura Outdoor",
      "DIY",
      "Tecnologia",
      "Viagem",
      "Restauro & Renovação",
      "Reviews de Produto",
      "Cinematografia com Drone",
    ],
  },
  trusted: {
    overline: "06 · confiam em nós",
    title: "Confiados por marcas internacionais de referência.",
    wordmarks: ["REOLINK", "ALLPOWERS", "EINHELL", "SOPREMA", "BARBOT", "BLUETTI"],
  },
  why: {
    overline: "07 · porquê a terra & trilhos",
    title: "Dez razões.",
    items: [
      "Storytelling Autêntico",
      "Cinematografia Profissional",
      "Conteúdo Long-form Perene",
      "Elevada Confiança da Audiência",
      "Integração Natural de Produto",
      "Produção com Drone",
      "Fotografia de Produto",
      "Cobertura de Eventos Internacionais",
      "Expertise no Mercado Português",
      "Audiência Europeia",
    ],
  },
  opps: {
    overline: "08 · oportunidades de parceria",
    title: "Formatos disponíveis.",
    items: [
      "Vídeo YouTube Dedicado",
      "Patrocínio Integrado",
      "Review de Produto",
      "Parceria de Longo Prazo",
      "Cobertura de Feira",
      "Booth Tour",
      "Cobertura de Evento",
      "Fotografia",
      "Conteúdo com Drone",
      "Instagram Reels",
      "YouTube Shorts",
      "Campanhas de Afiliação",
    ],
  },
  events: {
    overline: "09 · feiras internacionais",
    title: "Cobertura internacional de eventos.",
    text:
      "Disponíveis para feiras internacionais, lançamentos de produto, booth tours, entrevistas e storytelling em directo.",
    confirmed_label: "Confirmado",
    future_label: "Futuras edições",
    future: ["CES Las Vegas", "Eurobike", "Outdoor by ISPO"],
  },
  contact: {
    overline: "10 · vamos falar",
    title_l: "Vamos construir algo",
    title_r: "extraordinário juntos.",
    website_label: "Website",
    email_label: "Email",
    ig_label: "Instagram",
    website: "terraetrilhos.com",
    website_href: "https://terraetrilhos.com",
    email: "parcerias@terraetrilhos.com",
    ig: "@terraetrilhos",
    ig_href: "https://www.instagram.com/trilhosemfuria/",
    qr_hint: "Aponte a câmara · abrir o site",
  },
};

// Update EN hero title to match split pattern used across both languages
COPY_EN.hero.title_l = "Brand Partnership";
COPY_EN.hero.title_r = "Kit";

const COPY_MAP = { pt: COPY_PT, en: COPY_EN };

// Local context so every sub-component reads the right language copy
const KitCopyContext = createContext(COPY_EN);
const useKitCopy = () => useContext(KitCopyContext);

// SEO metadata per language
const KIT_META = {
  pt: {
    title: "Terra & Trilhos — Kit de Parcerias para Marcas",
    description:
      "Kit oficial de parcerias da Terra & Trilhos. Casa criativa portuguesa de storytelling premium para marcas internacionais.",
    ogTitle: "Terra & Trilhos — Kit de Parcerias",
    ogDescription:
      "Casa criativa portuguesa. Storytelling autêntico, cinematografia profissional e parcerias de longo prazo.",
  },
  en: {
    title: "Terra & Trilhos — Brand Partnership Kit",
    description:
      "Official Terra & Trilhos partnership kit. A Portuguese creative house producing premium storytelling for international brands.",
    ogTitle: "Terra & Trilhos — Brand Partnership Kit",
    ogDescription:
      "Portuguese creative house. Authentic storytelling, professional cinematography and long-term brand partnerships.",
  },
};



/* -------------------------------------------------------------------------- */
/*  Utilities                                                                  */
/* -------------------------------------------------------------------------- */

function useCountUp(target, duration = 1800, active = false, decimals = 0) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    const t0 = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(eased * target);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, active]);
  return decimals === 0 ? Math.round(val) : Number(val.toFixed(decimals));
}

const fmt = (n) => n.toLocaleString("en-US");

function useInView(threshold = 0.25) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      ([e]) => e.isIntersecting && setInView(true),
      { threshold }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* -------------------------------------------------------------------------- */
/*  Header                                                                     */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/*  Section head — consistent typography hierarchy                              */
/* -------------------------------------------------------------------------- */

function SectionHead({ overline, title, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className={`max-w-4xl ${className}`}
    >
      <p className="tt-overline">{overline}</p>
      <h2 className="mt-6 font-serif text-4xl md:text-6xl leading-[1.02] text-[var(--tt-text)]">
        {title}
      </h2>
    </motion.div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Hero                                                                       */
/* -------------------------------------------------------------------------- */

function KitHero() {
  const COPY = useKitCopy();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.3]);
  return (
    <section
      ref={ref}
      data-testid="kit-hero"
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={KIT_ASSETS.cover}
          alt="Terra & Trilhos"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(4,8,7,0.55) 0%, rgba(4,8,7,0.35) 45%, rgba(4,8,7,0.94) 100%)",
          }}
        />
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-12 h-[100svh] flex flex-col justify-end pb-20 md:pb-24 pt-40"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="tt-overline text-[var(--tt-gold)]"
        >
          {COPY.hero.eyebrow}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 font-serif font-light text-[11vw] md:text-[7.5vw] leading-[0.92] tracking-tighter text-[var(--tt-text)] max-w-[1200px]"
        >
          {COPY.hero.title_l}{" "}
          <em className="italic font-light text-[var(--tt-gold)]">{COPY.hero.title_r}</em>.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="mt-8 max-w-2xl text-[var(--tt-text-dim)] font-light text-base md:text-lg leading-relaxed"
        >
          {COPY.hero.subtitle}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.8 }}
          className="mt-12"
        >
          <a href="#kit-contact" data-testid="kit-hero-cta" className="tt-btn tt-btn-primary">
            {COPY.hero.cta}
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  01 · Who we are — reduced to two short paragraphs                          */
/* -------------------------------------------------------------------------- */

function WhoWeAre() {
  const COPY = useKitCopy();
  return (
    <section data-testid="kit-who" className="py-28 md:py-40 relative">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-24 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-6"
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={KIT_ASSETS.eduardo}
                alt="Eduardo Moreira"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent" />
              <div className="absolute bottom-8 left-8 right-8">
                <p className="tt-overline text-[var(--tt-gold)]">{COPY.who.location}</p>
                <p className="mt-3 font-serif italic text-2xl md:text-3xl text-white">
                  {COPY.who.role}
                </p>
              </div>
            </div>
          </motion.div>

          <div className="lg:col-span-6">
            <SectionHead overline={COPY.who.overline} title={COPY.who.title} />
            <div className="mt-12 space-y-6 text-[var(--tt-text-dim)] font-light leading-relaxed text-base md:text-lg max-w-xl">
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.1 }}
              >
                {COPY.who.p1}
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                {COPY.who.p2}
              </motion.p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  02 · Channels — minimal presentation, no duplicated numbers                */
/* -------------------------------------------------------------------------- */

function ChannelCard({ ch, imageSrc }) {
  return (
    <motion.a
      href={ch.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`kit-channel-${ch.name.replace(/\s+/g, "-").toLowerCase()}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="group relative block overflow-hidden aspect-[4/5]"
    >
      <img
        src={imageSrc}
        alt={ch.name}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-[1.05]"
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(4,8,7,0.15) 0%, rgba(4,8,7,0.9) 100%)",
        }}
      />
      <span
        className="absolute top-0 left-0 w-full h-px"
        style={{ background: `linear-gradient(90deg, ${ch.accent}, transparent)` }}
      />

      <div className="absolute top-8 left-8 right-8 flex items-start justify-between">
        <span className="tt-overline" style={{ color: ch.accent }}>
          {ch.handle}
        </span>
        <ArrowUpRight
          size={20}
          className="text-white/70 transition-all duration-500 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1"
        />
      </div>

      <div className="absolute bottom-8 left-8 right-8">
        <h3 className="font-serif text-4xl md:text-5xl text-white leading-[1]">
          {ch.name}
        </h3>
      </div>
    </motion.a>
  );
}

function VideoCard({ video, accent, index }) {
  const [playing, setPlaying] = useState(false);
  const thumb = `https://i.ytimg.com/vi/${video.id}/maxresdefault.jpg`;
  const fallback = `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`kit-video-${video.id}`}
      className="group relative overflow-hidden aspect-video bg-black"
    >
      {playing ? (
        <iframe
          title={video.title}
          src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0&modestbranding=1`}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          data-testid={`kit-video-play-${video.id}`}
          className="absolute inset-0 w-full h-full text-left"
          aria-label={`Play: ${video.title}`}
        >
          <img
            src={thumb}
            onError={(e) => {
              if (e.currentTarget.src !== fallback) e.currentTarget.src = fallback;
            }}
            alt={video.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.05]"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(4,8,7,0.05) 0%, rgba(4,8,7,0.85) 100%)",
            }}
          />
          <span
            className="absolute top-0 left-0 h-px w-full"
            style={{ background: `linear-gradient(90deg, ${accent}, transparent)` }}
          />
          <span className="absolute inset-0 grid place-items-center">
            <span
              className="grid place-items-center w-14 h-14 md:w-16 md:h-16 rounded-full border transition-all duration-500 group-hover:scale-110"
              style={{
                borderColor: "rgba(230,223,211,0.5)",
                background: "rgba(4,8,7,0.35)",
                backdropFilter: "blur(4px)",
              }}
            >
              <Play size={22} strokeWidth={1.5} className="text-white translate-x-[1px]" fill="currentColor" />
            </span>
          </span>
          <span className="absolute bottom-5 left-5 right-5">
            <span className="block font-serif text-base md:text-lg text-white leading-snug line-clamp-2">
              {video.title}
            </span>
          </span>
        </button>
      )}
    </motion.div>
  );
}

function FeaturedWork() {
  const COPY = useKitCopy();
  return (
    <div className="mt-28 md:mt-36" data-testid="kit-featured-work">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="max-w-3xl"
      >
        <p className="tt-overline text-[var(--tt-gold)]">{COPY.channels.work_title}</p>
        <p className="mt-4 text-[var(--tt-text-dim)] font-light leading-relaxed">
          {COPY.channels.work_caption}
        </p>
      </motion.div>

      {KIT_CHANNELS.map((kc) => {
        const src = CHANNELS.find((c) => c.name === kc.name);
        const videos = (src?.videos || []).slice(0, 3);
        if (!videos.length) return null;
        return (
          <div key={kc.name} className="mt-14">
            <div className="flex items-center gap-4 mb-8">
              <span
                className="w-2.5 h-2.5 rounded-full"
                style={{ background: kc.accent, boxShadow: `0 0 20px ${kc.accent}` }}
              />
              <p className="font-serif italic text-xl md:text-2xl text-[var(--tt-text)]">
                {kc.name}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {videos.map((v, i) => (
                <VideoCard key={v.id} video={v} accent={kc.accent} index={i} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Channels() {
  const COPY = useKitCopy();
  return (
    <section
      data-testid="kit-channels"
      className="py-28 md:py-40 relative"
      style={{ background: "var(--tt-surface)" }}
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <SectionHead overline={COPY.channels.overline} title={COPY.channels.title} />
        <p className="mt-6 max-w-xl text-[var(--tt-text-dim)] font-light leading-relaxed">
          {COPY.channels.caption}
        </p>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <ChannelCard ch={KIT_CHANNELS[0]} imageSrc={KIT_ASSETS.renovation} />
          <ChannelCard ch={KIT_CHANNELS[1]} imageSrc={KIT_ASSETS.mountain} />
        </div>

        <FeaturedWork />
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  03 · Audience                                                              */
/* -------------------------------------------------------------------------- */

function Audience() {
  const COPY = useKitCopy();
  return (
    <section data-testid="kit-audience" className="py-28 md:py-40 relative">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <SectionHead overline={COPY.audience.overline} title={COPY.audience.title} />
        <div className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-7"
          >
            <p className="tt-overline text-[var(--tt-gold)]">
              {COPY.audience.countries_label}
            </p>
            <ul className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-y-8 gap-x-8">
              {COPY.audience.countries.map((c, i) => (
                <motion.li
                  key={c}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.08 + i * 0.05 }}
                  className="flex items-baseline gap-4"
                >
                  <span className="font-serif italic text-[var(--tt-text-muted)] text-sm">
                    0{i + 1}
                  </span>
                  <span className="font-serif text-2xl md:text-3xl text-[var(--tt-text)]">
                    {c}
                  </span>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          <div className="lg:col-span-5 space-y-12">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="border-l border-[var(--tt-border-strong)] pl-6"
            >
              <p className="tt-overline text-[var(--tt-gold)]">
                {COPY.audience.demo_label}
              </p>
              <p className="mt-4 font-serif text-2xl md:text-3xl text-[var(--tt-text)] leading-tight">
                {COPY.audience.male}
                <br />
                <em className="italic text-[var(--tt-gold)]">{COPY.audience.age}</em>
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              <p className="tt-overline text-[var(--tt-gold)]">
                {COPY.audience.interests_label}
              </p>
              <ul className="mt-6 flex flex-wrap gap-2">
                {COPY.audience.interests.map((i) => (
                  <li
                    key={i}
                    className="tt-overline text-[10px] px-3 py-2 border border-[var(--tt-border)] text-[var(--tt-text-dim)]"
                  >
                    {i}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  04 · Performance                                                           */
/* -------------------------------------------------------------------------- */

function BigNumber({ display, label, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
      className="min-w-0"
    >
      <p
        className="font-serif text-[var(--tt-text)]"
        style={{
          fontSize: "clamp(48px, 4vw, 72px)",
          fontWeight: 500,
          lineHeight: 0.95,
          letterSpacing: "-0.03em",
          whiteSpace: "nowrap",
        }}
      >
        {display}
      </p>
      <p
        className="text-[var(--tt-text-muted)]"
        style={{
          marginTop: "14px",
          fontSize: "11px",
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          lineHeight: 1.4,
          fontFamily: "'Outfit', sans-serif",
          fontWeight: 500,
        }}
      >
        {label}
      </p>
    </motion.div>
  );
}

function CTRGauge({ ch, delay = 0 }) {
  const COPY = useKitCopy();
  const { lang } = useT();
  const [ref, inView] = useInView(0.5);
  const val = useCountUp(ch.ctr, 1600, inView, 1);
  const pct = (ch.ctr / 12) * 100;
  const displayVal = lang === "pt" ? String(val).replace(".", ",") : val;
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.9, delay }}
      className="flex items-center gap-6"
    >
      <div className="relative w-24 h-24 md:w-28 md:h-28 shrink-0">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle
            cx="50"
            cy="50"
            r="42"
            fill="none"
            stroke="var(--tt-border-strong)"
            strokeWidth="3"
          />
          <motion.circle
            cx="50"
            cy="50"
            r="42"
            fill="none"
            stroke={ch.accent}
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={2 * Math.PI * 42}
            initial={{ strokeDashoffset: 2 * Math.PI * 42 }}
            animate={inView ? { strokeDashoffset: 2 * Math.PI * 42 * (1 - pct / 100) } : {}}
            transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
          />
        </svg>
        <div className="absolute inset-0 grid place-items-center">
          <span className="font-serif text-xl md:text-2xl text-[var(--tt-text)]">
            {displayVal}%
          </span>
        </div>
      </div>
      <div className="max-w-md">
        <p className="tt-overline" style={{ color: ch.accent }}>
          {COPY.performance.labels.ctr}
        </p>
        <p className="mt-1 font-serif italic text-lg text-[var(--tt-text-dim)]">
          {ch.name}
        </p>
        <p className="mt-2 text-[var(--tt-text-muted)] font-light text-xs leading-relaxed">
          {COPY.performance.ctr_desc}
        </p>
      </div>
    </motion.div>
  );
}

function Performance() {
  const COPY = useKitCopy();
  const L = COPY.performance.labels;
  return (
    <section
      data-testid="kit-performance"
      className="py-28 md:py-40 relative"
      style={{ background: "var(--tt-surface)" }}
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <SectionHead
          overline={COPY.performance.overline}
          title={COPY.performance.title}
        />
        <p className="mt-6 tt-overline text-[var(--tt-text-muted)]">
          {COPY.performance.caption}
        </p>

        {KIT_CHANNELS.map((ch, idx) => (
          <div
            key={ch.name}
            className={`mt-24 pt-16 ${
              idx > 0 ? "border-t border-[var(--tt-border)]" : ""
            }`}
            data-testid={`kit-perf-${ch.name.replace(/\s+/g, "-").toLowerCase()}`}
          >
            <div className="flex items-center gap-4 mb-14">
              <span
                className="w-2.5 h-2.5 rounded-full"
                style={{ background: ch.accent, boxShadow: `0 0 20px ${ch.accent}` }}
              />
              <p className="font-serif italic text-2xl md:text-3xl text-[var(--tt-text)]">
                {ch.name}
              </p>
            </div>

            <div
              className="kit-metrics-grid grid"
              style={{
                gridTemplateColumns: "1fr",
                gap: "24px",
              }}
            >
              <BigNumber display={ch.display.subs} label={L.subs} />
              <BigNumber display={ch.display.views} label={L.views} delay={0.05} />
              <BigNumber display={ch.display.watch} label={L.watch} delay={0.1} />
              <BigNumber display={ch.display.impressions} label={L.impressions} delay={0.15} />
            </div>

            <div className="mt-16 pt-12 border-t border-[var(--tt-border)]">
              <CTRGauge ch={ch} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  05 · Content grid                                                          */
/* -------------------------------------------------------------------------- */

function ContentGrid() {
  const COPY = useKitCopy();
  const layout = [
    { img: KIT_ASSETS.mountain, span: "md:col-span-2 md:row-span-2" },
    { img: KIT_ASSETS.diy, span: "" },
    { img: KIT_ASSETS.technologyTile, span: "" },
    { img: KIT_ASSETS.travel, span: "md:col-span-2" },
    { img: KIT_ASSETS.restoration, span: "" },
    { img: KIT_ASSETS.productReviews, span: "" },
    { img: KIT_ASSETS.droneCine, span: "md:col-span-2" },
  ];
  return (
    <section data-testid="kit-content" className="py-28 md:py-40 relative">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <SectionHead overline={COPY.content.overline} title={COPY.content.title} />
        <div className="mt-20 grid grid-cols-1 md:grid-cols-4 auto-rows-[320px] md:auto-rows-[240px] lg:auto-rows-[280px] gap-4 md:gap-6">
          {COPY.content.pillars.map((p, i) => {
            const l = layout[i] || {};
            return (
              <motion.figure
                key={p}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`kit-content-${i}`}
                className={`group relative overflow-hidden ${l.span || ""}`}
              >
                <img
                  src={l.img}
                  alt={p}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                <figcaption className="absolute bottom-6 left-6 right-6">
                  <p className="tt-overline text-[var(--tt-gold)] text-[10px]">
                    0{i + 1}
                  </p>
                  <p className="mt-3 font-serif text-xl md:text-2xl lg:text-3xl text-white leading-tight">
                    {p}
                  </p>
                </figcaption>
              </motion.figure>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  06 · Trusted by                                                            */
/* -------------------------------------------------------------------------- */

function TrustedKit() {
  const COPY = useKitCopy();
  return (
    <section
      data-testid="kit-trusted"
      className="relative border-y border-[var(--tt-border)] py-24 md:py-28"
      style={{ background: "var(--tt-surface)" }}
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="tt-overline">{COPY.trusted.overline}</p>
          <h3 className="mt-5 font-serif italic text-[var(--tt-text-dim)] text-2xl md:text-3xl">
            {COPY.trusted.title}
          </h3>
        </motion.div>
        <motion.ul
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.15 }}
          className="flex flex-wrap items-center justify-between gap-y-12 gap-x-6"
        >
          {BRAND_LOGOS.map((b, i) => (
            <li
              key={b.name}
              className="flex-1 min-w-[130px] flex items-center justify-center"
              data-testid={`kit-trusted-${b.name.toLowerCase()}`}
            >
              <motion.img
                src={b.file}
                alt={b.name}
                loading="lazy"
                draggable={false}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 0.6, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 + i * 0.05 }}
                whileHover={{ opacity: 1 }}
                style={{ height: b.h, width: "auto" }}
                className="select-none object-contain transition-opacity duration-500"
              />
            </li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  07 · Why brands choose us                                                  */
/* -------------------------------------------------------------------------- */

const WHY_ICONS = [
  Sparkles, Film, Video, ShieldCheck, Award,
  Camera, Palette, MapPin, Users, GlobeIcon,
];

function WhyUs() {
  const COPY = useKitCopy();
  return (
    <section data-testid="kit-why" className="py-28 md:py-40 relative">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <SectionHead overline={COPY.why.overline} title={COPY.why.title} />
        <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-10 gap-y-14">
          {COPY.why.items.map((it, i) => {
            const Icon = WHY_ICONS[i] || Sparkles;
            return (
              <motion.div
                key={it}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.04 }}
                data-testid={`kit-why-${i}`}
                className="group"
              >
                <Icon
                  size={22}
                  strokeWidth={1.3}
                  className="text-[var(--tt-gold)] transition-transform duration-500 group-hover:-translate-y-1"
                />
                <p className="mt-6 font-serif text-xl md:text-2xl text-[var(--tt-text)] leading-tight">
                  {it}
                </p>
                <div className="mt-5 h-px w-8 bg-[var(--tt-border-strong)] transition-all duration-500 group-hover:w-16 group-hover:bg-[var(--tt-gold)]" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  08 · Partnership opportunities                                             */
/* -------------------------------------------------------------------------- */

function Opportunities() {
  const COPY = useKitCopy();
  return (
    <section
      data-testid="kit-opps"
      className="py-28 md:py-40 relative"
      style={{ background: "var(--tt-surface)" }}
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <SectionHead overline={COPY.opps.overline} title={COPY.opps.title} />
        <ul className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-12">
          {COPY.opps.items.map((it, i) => (
            <motion.li
              key={it}
              initial={{ opacity: 0, x: -12 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.04 }}
              data-testid={`kit-opp-${i}`}
              className="group flex items-baseline gap-5 py-5 border-b border-[var(--tt-border)]"
            >
              <span className="font-serif italic text-[var(--tt-text-muted)] text-sm w-8">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="font-serif text-xl md:text-2xl text-[var(--tt-text)] leading-tight transition-colors duration-500 group-hover:text-[var(--tt-gold)]">
                {it}
              </span>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  09 · Trade shows                                                           */
/* -------------------------------------------------------------------------- */

function TradeShows() {
  const COPY = useKitCopy();
  return (
    <section data-testid="kit-events" className="py-28 md:py-40 relative">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <SectionHead overline={COPY.events.overline} title={COPY.events.title} />
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-6 text-[var(--tt-text-dim)] font-light leading-relaxed text-base md:text-lg"
          >
            {COPY.events.text}
          </motion.p>

          <div className="lg:col-span-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="relative rounded-sm tt-trace-border"
            >
              <div className="tt-glass p-10 h-full">
                <p className="tt-overline text-[var(--tt-gold)]">
                  {COPY.events.confirmed_label}
                </p>
                <p className="mt-6 font-serif text-4xl md:text-5xl text-[var(--tt-text)]">
                  IFA
                </p>
                <p className="font-serif italic text-lg text-[var(--tt-text-dim)]">Berlin</p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative tt-glass p-10 border border-[var(--tt-border)]"
            >
              <p className="tt-overline">{COPY.events.future_label}</p>
              <ul className="mt-6 space-y-3">
                {COPY.events.future.map((v) => (
                  <li key={v} className="font-serif italic text-lg text-[var(--tt-text)]">
                    {v}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  10 · Contact                                                               */
/* -------------------------------------------------------------------------- */

function ContactLine({ icon, label, value, href, testid }) {
  return (
    <a
      href={href}
      target={href?.startsWith("http") ? "_blank" : undefined}
      rel="noopener noreferrer"
      data-testid={testid}
      className="group flex items-center gap-6 border-b border-[var(--tt-border)] pb-5"
    >
      <span className="text-[var(--tt-gold)] w-8">{icon}</span>
      <div className="flex-1 flex flex-col md:flex-row md:items-baseline md:justify-between gap-1 md:gap-6">
        <span className="tt-overline text-[var(--tt-text-muted)]">{label}</span>
        <span className="font-serif italic text-xl md:text-2xl text-[var(--tt-text)] transition-colors duration-500 group-hover:text-[var(--tt-gold)]">
          {value}
        </span>
      </div>
      <ArrowUpRight
        size={18}
        className="text-[var(--tt-text-muted)] transition-all duration-500 group-hover:text-[var(--tt-text)] group-hover:translate-x-1 group-hover:-translate-y-1"
      />
    </a>
  );
}

function KitContact() {
  const COPY = useKitCopy();
  const qr = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(
    COPY.contact.website_href
  )}&size=280x280&margin=8&color=E6DFD3&bgcolor=040807&qzone=1`;
  return (
    <section
      id="kit-contact"
      data-testid="kit-contact"
      className="relative min-h-[100svh] flex items-end overflow-hidden"
    >
      <img
        src={KIT_ASSETS.contact}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(4,8,7,0.45) 0%, rgba(4,8,7,0.65) 55%, rgba(4,8,7,0.95) 100%)",
        }}
      />
      <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-12 pb-24 md:pb-32 pt-32 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
        >
          <p className="tt-overline text-[var(--tt-gold)]">{COPY.contact.overline}</p>
          <h2 className="mt-6 font-serif font-light text-[10vw] md:text-[6.5vw] leading-[0.92] tracking-tighter text-[var(--tt-text)] max-w-[1200px]">
            {COPY.contact.title_l}{" "}
            <em className="italic text-[var(--tt-gold)]">{COPY.contact.title_r}</em>
          </h2>
        </motion.div>

        <div className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-14 items-end">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-7 space-y-6"
          >
            <ContactLine
              icon={<GlobeIcon size={16} strokeWidth={1.4} />}
              label={COPY.contact.website_label}
              value={COPY.contact.website}
              href={COPY.contact.website_href}
              testid="kit-website"
            />
            <ContactLine
              icon={<Mail size={16} strokeWidth={1.4} />}
              label={COPY.contact.email_label}
              value={COPY.contact.email}
              href={`mailto:${COPY.contact.email}`}
              testid="kit-email"
            />
            <ContactLine
              icon={<Instagram size={16} strokeWidth={1.4} />}
              label={COPY.contact.ig_label}
              value={COPY.contact.ig}
              href={COPY.contact.ig_href}
              testid="kit-instagram"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="lg:col-span-5 flex lg:justify-end"
          >
            <div className="flex flex-col items-center gap-4">
              <div className="p-3 bg-[var(--tt-bg)] border border-[var(--tt-border-strong)] rounded-sm">
                <img
                  src={qr}
                  alt="QR code — terraetrilhos.com"
                  className="w-40 h-40 md:w-44 md:h-44"
                  loading="lazy"
                  data-testid="kit-qr"
                />
              </div>
              <p className="tt-overline text-[var(--tt-text-muted)] text-center max-w-[220px]">
                {COPY.contact.qr_hint}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */

export default function BrandKitPage() {
  useLenis();
  const { lang } = useT();
  const COPY = COPY_MAP[lang] || COPY_EN;
  const meta = KIT_META[lang] || KIT_META.en;

  useEffect(() => {
    document.title = meta.title;
    document.documentElement.lang = lang === "pt" ? "pt-PT" : "en";

    // description
    let desc = document.querySelector('meta[name="description"]');
    if (!desc) {
      desc = document.createElement("meta");
      desc.setAttribute("name", "description");
      document.head.appendChild(desc);
    }
    desc.setAttribute("content", meta.description);

    // og:title
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement("meta");
      ogTitle.setAttribute("property", "og:title");
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute("content", meta.ogTitle);

    // og:description
    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (!ogDesc) {
      ogDesc = document.createElement("meta");
      ogDesc.setAttribute("property", "og:description");
      document.head.appendChild(ogDesc);
    }
    ogDesc.setAttribute("content", meta.ogDescription);

    // canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://terraetrilhos.com/brand-partnership-kit");
  }, [lang, meta]);

  return (
    <KitCopyContext.Provider value={COPY}>
      <main
        data-testid="kit-page"
        className="min-h-screen bg-[var(--tt-bg)] text-[var(--tt-text)]"
      >
        <Navigation />
        <KitHero />
        <WhoWeAre />
        <Channels />
        <Audience />
        <Performance />
        <ContentGrid />
        <TrustedKit />
        <WhyUs />
        <Opportunities />
        <TradeShows />
        <KitContact />

        <footer className="border-t border-[var(--tt-border)] py-10">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-[var(--tt-text-muted)]">
            <p>
              © {new Date().getFullYear()} {SITE.company}. {lang === "pt" ? "Todos os direitos reservados." : "All rights reserved."}
            </p>
            <p className="tt-overline">{COPY.header.meta}</p>
          </div>
        </footer>
      </main>
    </KitCopyContext.Provider>
  );
}
